import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Megaphone, Sparkles } from "lucide-react";
import { useAdImpression, trackAdClick } from "@/lib/adTracking";

/**
 * Horizontal sponsored strip that sits right below the header nav.
 * Fetches ads from slot="nav-sponsored". If none configured, shows a
 * subtle "sponsor space available" placeholder so the strip is always
 * visible (per user request).
 */
export default function SponsoredStrip() {
  const [ads, setAds] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    api.get("/ads", { params: { slot: "nav-sponsored" } })
      .then((r) => setAds(Array.isArray(r.data) ? r.data : []))
      .catch(() => setAds([]));
  }, []);

  // Rotate ads every 6 s when more than one is active.
  useEffect(() => {
    if (ads.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % ads.length), 6000);
    return () => clearInterval(t);
  }, [ads.length]);

  const ad = ads[idx];
  useAdImpression(ad?.ad_id);

  if (!ad) {
    // Empty-state placeholder — communicates to admins that the slot exists.
    return (
      <div
        className="w-full border-b border-border/60 bg-gradient-to-r from-amber-50 via-orange-50 to-rose-50 dark:from-amber-950/20 dark:via-orange-950/20 dark:to-rose-950/20"
        data-testid="sponsored-strip-empty"
      >
        <div className="mx-auto max-w-screen-2xl px-4 sm:px-6 lg:px-8 h-11 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-[11px] text-muted-foreground min-w-0">
            <span className="uppercase tracking-[0.18em] text-[10px] font-semibold text-amber-700 shrink-0 inline-flex items-center gap-1">
              <Megaphone className="h-3 w-3" /> Sponsored
            </span>
            <span className="truncate">Sponsor spot open — reach every community member the moment they log in.</span>
          </div>
          <Link
            to="/vendor/dashboard?tab=ads"
            className="text-[11px] font-semibold text-amber-800 dark:text-amber-300 hover:underline shrink-0 inline-flex items-center gap-1"
            data-testid="sponsored-strip-cta"
          >
            <Sparkles className="h-3 w-3" /> Advertise here
          </Link>
        </div>
      </div>
    );
  }

  const bg = ad.background || "linear-gradient(90deg,#fef3c7 0%,#fed7aa 50%,#fecdd3 100%)";
  const fg = ad.color || "#7c2d12";

  return (
    <div
      className="w-full border-b border-border/60"
      style={{ background: bg }}
      data-testid={`sponsored-strip-${ad.ad_id}`}
    >
      <div className="mx-auto max-w-screen-2xl px-4 sm:px-6 lg:px-8 h-11 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0" style={{ color: fg }}>
          <span
            className="uppercase tracking-[0.18em] text-[10px] font-semibold shrink-0 inline-flex items-center gap-1"
            style={{ opacity: 0.7 }}
          >
            <Megaphone className="h-3 w-3" /> Sponsored
          </span>
          <span className="truncate text-xs font-medium">
            {ad.headline || ad.title || "Featured sponsor"}
          </span>
          {ad.subtitle && (
            <span className="truncate text-[11px] hidden md:inline" style={{ opacity: 0.75 }}>
              · {ad.subtitle}
            </span>
          )}
        </div>
        {ad.cta_url && (
          <a
            href={ad.cta_url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackAdClick(ad.ad_id)}
            className="text-[11px] font-semibold shrink-0 inline-flex items-center gap-1 hover:underline"
            style={{ color: fg }}
            data-testid={`sponsored-strip-cta-${ad.ad_id}`}
          >
            {ad.cta_label || "Learn more"} →
          </a>
        )}
      </div>
    </div>
  );
}
